import { useState, useEffect, useCallback, useRef } from 'react';
import { getUserStats } from '../../../api/userApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users2, BookMarkedIcon, GraduationCap } from 'lucide-react';
import dashboardBanner from '../../../assets/dashboard-banner.png';

function ErrorDisplay({ error, onRetry }) {
  return (
    <div className="!bg-red-100 border-l-4 !border-red-400 p-4 mb-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-red-700">{error ?? "Something went wrong!"}</p>
          {onRetry && (
            <div className="mt-2">
              <button
                onClick={onRetry}
                className="text-sm font-medium text-red-700 hover:text-red-600 focus:outline-none focus:underline transition duration-150 ease-in-out"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// `userGrowth` arrives as [{ month: '2026-03', count: 12 }, ...], oldest first.
const toUserGrowthSeries = (userGrowth) =>
  (userGrowth ?? []).map(({ month, count }) => {
    const [year, monthIndex] = month.split('-');
    return {
      name: new Date(Number(year), Number(monthIndex) - 1, 1).toLocaleString(
        'default',
        { month: 'short' }
      ),
      users: count,
    };
  });

// The KPIs are precomputed every 15 min by a server cron. Right after a
// deploy the first run takes a minute or two; until then the API reports
// `pending` and the page polls quietly.
const PENDING_POLL_MS = 15_000;
const PENDING_MAX_POLLS = 12;

const formatUpdatedAt = (computedAt) => {
  if (!computedAt) return '';
  const minutes = Math.floor((Date.now() - new Date(computedAt).getTime()) / 60_000);
  if (minutes < 1) return 'Updated just now';
  if (minutes < 60) return `Updated ${minutes} min ago`;
  return `Updated ${new Date(computedAt).toLocaleString()}`;
};

const toCourseStatusSeries = (courseStatus) => [
  { name: 'Active', value: courseStatus?.active ?? 0, color: '#10B981' },
  { name: 'Draft', value: courseStatus?.draft ?? 0, color: '#3B82F6' },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalUsers: null,
    verifiedUsers: null,
    usersCompletedAllCourses: null,
    usersNotCompleted: null,
  });
  const [pending, setPending] = useState(false);
  const [computedAt, setComputedAt] = useState(null);
  const pollsRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [courseStatusData, setCourseStatusData] = useState([]);
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [isPieChartLoading, setIsPieChartLoading] = useState(true);

  // One request for the whole dashboard. GET /api/users/stats only reads the
  // precomputed `analytics_snapshots` row, so it is fast whatever the data
  // size. `silent` refreshes (polling while pending) keep the page as it is
  // instead of flashing every card back to "-".
  const fetchDashboard = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setIsChartLoading(true);
      setIsPieChartLoading(true);
    }
    try {
      const response = await getUserStats();
      if (!response?.success) {
        throw new Error(response?.message || 'Unexpected dashboard response');
      }
      const isPending = Boolean(response.pending);
      setStats({
        totalUsers: response.totalUsers ?? null,
        verifiedUsers: response.verifiedUsers ?? null,
        usersCompletedAllCourses: response.usersCompletedAllCourses ?? null,
        usersNotCompleted: response.usersNotCompleted ?? null,
      });
      setPending(isPending);
      setComputedAt(response.computedAt ?? null);
      setUserGrowthData(toUserGrowthSeries(response.userGrowth));
      setCourseStatusData(toCourseStatusSeries(response.courseStatus));
      // Charts keep their loading placeholders until real figures exist.
      setIsChartLoading(isPending);
      setIsPieChartLoading(isPending);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
      // A failed background poll shouldn't replace a working dashboard.
      if (!silent) setError('Failed to load dashboard statistics');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (!pending) {
      pollsRef.current = 0;
      return undefined;
    }
    if (pollsRef.current >= PENDING_MAX_POLLS) return undefined;
    const timer = setTimeout(() => {
      pollsRef.current += 1;
      fetchDashboard({ silent: true });
    }, PENDING_POLL_MS);
    return () => clearTimeout(timer);
  }, [pending, stats, fetchDashboard]);

  const kpi = (value) =>
    loading || value === null || value === undefined ? (
      <span className={pending ? 'animate-pulse' : undefined}>-</span>
    ) : (
      value
    );

  const handleRetry = () => {
    setError(null);
    fetchDashboard();
  };

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <ErrorDisplay error={error} onRetry={handleRetry} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && <ErrorDisplay error={error} onRetry={handleRetry} />}
      <div className="relative mb-6 w-full h-[220px] rounded-3xl overflow-hidden flex items-stretch !font-helvetica">
        <img
          src={dashboardBanner}
          alt="Dashboard Banner"
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
          draggable={false}
        />
        <div className="relative z-20 flex flex-1 items-center gap-4 lg:gap-10 px-4 lg:pl-10 lg:pr-6 py-8">
          <div className="flex-1 flex flex-col justify-center">
            <h2 className="text-2xl lg:text-[3rem] font-bold text-white drop-shadow-lg">
              {"Overview"}
            </h2>
            <p className="text-white/90 text-sm lg:text-base max-w-md drop-shadow">
              {"Insights from your training platform"}
            </p>
            {!loading && (pending || computedAt) && (
              <p className="text-white/75 text-xs lg:text-sm mt-1 drop-shadow">
                {pending ? 'Calculating the latest figures…' : formatUpdatedAt(computedAt)}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4 min-w-0 lg:min-w-[540px]">
            <div className="glass rounded-2xl lg:rounded-3xl px-3 lg:px-8 py-3 lg:py-6 flex flex-col items-start min-w-0">
              <span className="text-xl lg:text-[3rem] font-bold text-white drop-shadow truncate">
                {kpi(stats.totalUsers)}
              </span>
              <span className="flex gap-1 lg:gap-2 items-center text-xs lg:text-[1rem] text-white">
                <Users2 className="h-3 w-3 lg:h-4 lg:w-4 text-white fill-white flex-shrink-0" />
                <span className="truncate">{"Total Users"}</span>
              </span>
            </div>
            <div className="glass rounded-2xl lg:rounded-3xl px-3 lg:px-8 py-3 lg:py-6 flex flex-col items-start min-w-0">
              <span className="text-xl lg:text-[3rem] font-bold text-white drop-shadow truncate">
                {kpi(stats.verifiedUsers)}
              </span>
              <span className="flex gap-1 lg:gap-2 items-center text-xs lg:text-[1rem] text-white">
                <BookMarkedIcon className="h-3 w-3 lg:h-4 lg:w-4 text-white flex-shrink-0" />
                <span className="truncate">{"Verified Users"}</span>
              </span>
            </div>
            <div className="glass rounded-2xl lg:rounded-3xl px-3 lg:px-8 py-3 lg:py-6 flex flex-col items-start min-w-0">
              <span className="text-xl lg:text-[3rem] font-bold text-white drop-shadow truncate">
                {kpi(stats.usersCompletedAllCourses)}
              </span>
              <span className="flex gap-1 lg:gap-2 items-center text-xs lg:text-[1rem] text-white">
                <GraduationCap className="h-3 w-3 lg:h-4 lg:w-4 text-white flex-shrink-0" />
                <span className="truncate">{"All courses certified"}</span>
              </span>
            </div>
            <div className="glass rounded-2xl lg:rounded-3xl px-3 lg:px-8 py-3 lg:py-6 flex flex-col items-start min-w-0">
              <span className="text-xl lg:text-[3rem] font-bold text-[var(--tertiary-font)] drop-shadow truncate">
                {kpi(stats.usersNotCompleted)}
              </span>
              <span className="text-xs lg:text-[1rem] text-white truncate">{"In Progress"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Cards Section */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-7 xl:grid-cols-7">
        <Card className="lg:col-span-4 xl:col-span-4">
          <CardHeader>
            <CardTitle className="text-lg lg:text-xl">User Growth</CardTitle>
            <CardDescription className="text-sm lg:text-base">
              New users over the last 6 months
            </CardDescription>
          </CardHeader>
          <CardContent className="pl-2">
            <div className="h-[250px] sm:h-[300px] lg:h-[350px] xl:h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={isChartLoading ? Array(6).fill({ name: 'Loading...', users: 0 }) : userGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `${value}`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      borderRadius: '0.5rem', 
                      border: '1px solid #e5e7eb',
                      fontSize: '14px'
                    }}
                    itemStyle={{ color: '#1f2937' }}
                  />
                  <Bar 
                    dataKey="users"
                    fill="#3b82f6"
                    radius={[4, 4, 0, 0]}
                    className={isChartLoading ? 'opacity-50' : 'fill-primary'}
                    animationDuration={isChartLoading ? 0 : 1500}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3 xl:col-span-3">
          <CardHeader>
            <CardTitle className="text-lg lg:text-xl">Course Status</CardTitle>
            <CardDescription className="text-sm lg:text-base">
              Overall course completion status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px] sm:h-[300px] lg:h-[350px] xl:h-[400px] flex flex-col items-center justify-center">
              <div className="w-[150px] h-[150px] sm:w-[180px] sm:h-[180px] lg:w-[220px] lg:h-[220px] xl:w-[250px] xl:h-[250px] mb-4 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={isPieChartLoading ? [{ name: 'Loading...', value: 1 }] : courseStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius="45%"
                      outerRadius="75%"
                      paddingAngle={5}
                      dataKey="value"
                      animationDuration={isPieChartLoading ? 0 : 1500}
                    >
                      {isPieChartLoading ? (
                        <Cell key="loading" fill="#E5E7EB" />
                      ) : (
                        courseStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))
                      )}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [
                        value,
                        `${name}: ${value} (${courseStatusData.length > 0 ? Math.round((value / courseStatusData.reduce((acc, curr) => acc + curr.value, 0)) * 100) : 0}%)`
                      ]}
                      itemStyle={{ color: '#1f2937' }}
                      contentStyle={{ fontSize: '14px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 w-full px-2">
                {isPieChartLoading ? (
                  <div className="text-sm text-gray-500 text-center">Loading course status...</div>
                ) : (
                  courseStatusData.map((status, index) => (
                    <div key={index} className="flex items-center justify-center sm:justify-start">
                      <div 
                        className="w-3 h-3 rounded-full mr-2 flex-shrink-0" 
                        style={{ backgroundColor: status.color }}
                      />
                      <span className="text-sm lg:text-base text-gray-600 text-center sm:text-left">
                        {status.name}: {status.value} ({courseStatusData.length > 0 ? Math.round((status.value / courseStatusData.reduce((acc, curr) => acc + curr.value, 0)) * 100) : 0}%)
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}